import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { connect, useDispatch } from "react-redux";
import {
  fetchUsersWithAccess,
  setShowSharingMenu,
  submitRevokeAccessRequest,
  submitRevokeInvitationRequest,
  submitShareInviteRequest,
} from "redux/SingleDataset/actions";
import {
  getDataset,
  getInvitedEmails,
  getShowDatasetSharingMenu,
  getUsersWithAccess,
} from "redux/SingleDataset/selectors";
import classNames from "classnames";
import styles from "./style.module.scss";
import { State } from "redux/store";
import { isValidEmail, useWindowResize } from "helpers/utility/utilities";
import PublicDatasetIcon from "assets/icons/PublicDatasetIcon";
import InboxIcon from "assets/icons/InboxIcon";
import ChevronIcon from "assets/icons/ChevronIcon";
import vllogo from "assets/img/vllogo-with-label.svg";
import { Id, toast } from "react-toastify";
import VLToastComponent from "views/components/VLToastComponent";
import { MemberWithRole } from "types";
import { UserRow } from "views/components/SharingMenu/UserRow";
import { InviteInput } from "views/components/SharingMenu/InviteInput";
import { RightSlideMenu } from "views/components/SharingMenu/RightSlideMenu";
import VLAvatar from "views/uikit/VLAvatar";
import ShareIcon from "assets/icons/ShareIcon";

interface SharingMenuProps {
  showSharingMenu: boolean;
  dataset: any;
  usersWithAccess: MemberWithRole[];
  invitedEmails: string[];
}

const DatasetSharingMenu = ({
  showSharingMenu,
  dataset,
  usersWithAccess,
  invitedEmails,
}: SharingMenuProps) => {
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [insertedEmail, setInsertedEmail] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [placeholdersCount, setPlaceholdersCount] = useState<number>(0);
  const [isInvitedListOpen, setIsInvitedListOpen] = useState(true);
  const [isCollaboratorsListOpen, setIsCollaboratorsListOpen] = useState(true);
  const { height } = useWindowResize();

  useEffect(() => {
    if (insertedEmail.length === 0) {
      setErrorMsg("");
    } else if (!isValidEmail(insertedEmail)) {
      setErrorMsg("Please enter a valid email.");
    } else if (invitedEmails.includes(insertedEmail)) {
      setErrorMsg("We've already sent this email an invitation.");
    } else if (
      usersWithAccess.map((member) => member.user.email).includes(insertedEmail)
    ) {
      setErrorMsg("This user is already a collaborator.");
    } else {
      setErrorMsg("");
    }
  }, [insertedEmail, invitedEmails, usersWithAccess]);

  useEffect(() => {
    if (showSharingMenu) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      setInsertedEmail("");
    };
    //eslint-disable-next-line
  }, [showSharingMenu]);

  useEffect(() => {
    if (dataset?.id && !dataset?.sample && showSharingMenu) {
      dispatch(fetchUsersWithAccess(dataset?.id) as any);
    }
  }, [showSharingMenu, dataset, dispatch]);

  useEffect(() => {
    const menuContainer = containerRef.current;
    let totalHeight;
    if (menuContainer) totalHeight = menuContainer.offsetHeight - 330;
    if (totalHeight) {
      // 67 is a single element's height,
      let requiredCount = Math.round(totalHeight / 67);

      if (usersWithAccess.length + invitedEmails.length < requiredCount) {
        setPlaceholdersCount(
          requiredCount - usersWithAccess.length - invitedEmails.length,
        );
      } else {
        setPlaceholdersCount(0);
      }
    }
  }, [showSharingMenu, usersWithAccess, invitedEmails, containerRef, height]);

  const inviteBtnDisabled = useMemo(() => {
    return !insertedEmail || errorMsg !== "";
  }, [insertedEmail, errorMsg]);
  const inputDisabled = useMemo(() => {
    return errorMsg.length > 0;
  }, [errorMsg]);
  const usersWithoutOwner = useMemo(() => {
    return usersWithAccess.filter(
      (member) =>
        dataset?.created_by && dataset?.created_by !== member.user.user_id,
    );
  }, [usersWithAccess, dataset?.created_by]);
  function handleOutsideClick(event: MouseEvent) {
    if (
      containerRef.current &&
      !containerRef.current.contains(event.target as Node)
    ) {
      dispatch(setShowSharingMenu(false));
    }
  }

  const handleCloseIconClick = () => {
    dispatch(setShowSharingMenu(false));
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setInsertedEmail(value.trim());
  };

  const handleSendInvitation = async () => {
    const response = await dispatch(
      submitShareInviteRequest(dataset?.id, insertedEmail) as any,
    );
    if (response.status !== 200) {
      setErrorMsg("Couldn't invite user, please try again later.");
    } else {
      setInsertedEmail("");
      if (dataset?.sample) {
        successNotification();
      } else {
        await dispatch(fetchUsersWithAccess(dataset?.id) as any);
      }
    }
  };

  const toastIdRef = useRef<any>(null);

  const showToast = (Element: ReactNode): Id => {
    return toast(Element, {
      closeButton: false,
      closeOnClick: false,
      position: "bottom-center",
    });
  };

  const successNotification = () => {
    toastIdRef.current = showToast(
      <VLToastComponent
        status="SUCCESS"
        title={"You’ve successfully invited 1 person to view the dataset."}
        onClose={() => toast.dismiss(toastIdRef.current)}
        description={""}
      />,
    );
  };

  const renderOwnerRow = () => {
    if (!dataset?.created_by && !dataset?.sample) {
      return <noscript />;
    }
    let ownerUser;
    if (dataset?.sample) {
      ownerUser = {
        email: "support@visual-layer.com",
        name: "Visual Layer",
      };
    } else {
      const ownerMember = usersWithAccess.find(
        (member) => member.user.user_id === dataset?.created_by,
      );
      ownerUser = ownerMember?.user;
    }
    if (!ownerUser) {
      return <noscript />;
    }
    const vlAvatar = (
      <div>
        <img src={vllogo} alt="logo" height={"39px"} width={"39px"} />
      </div>
    );
    const userAvatar = (
      <div>
        <VLAvatar src={ownerUser.avatar_uri} />
      </div>
    );
    return (
      <UserRow
        email={ownerUser.email}
        name={ownerUser.name}
        avatarIcon={dataset?.sample ? vlAvatar : userAvatar}
        subtitle="Dataset Owner"
        classname={styles.datasetOwnerRow}
        dataTestId="owner-row"
      />
    );
  };

  const onRemoveInvite = async (email: string) => {
    await dispatch(submitRevokeInvitationRequest(dataset?.id, email) as any);
    await dispatch(fetchUsersWithAccess(dataset?.id) as any);
  };

  const onRemoveCollaborator = async (userId?: string) => {
    if (!userId) {
      return;
    }
    await dispatch(submitRevokeAccessRequest(dataset?.id, userId) as any);
    await dispatch(fetchUsersWithAccess(dataset?.id) as any);
  };

  const renderInvitations = () => {
    if (dataset?.sample) {
      return <noscript />;
    }
    return (
      <>
        <div
          className={classNames(styles.collaboratorsTitle, {
            [styles.expandableTitle]: invitedEmails.length > 0,
          })}
          onClick={() => setIsInvitedListOpen(!isInvitedListOpen)}
        >
          <InboxIcon />
          Invited ({invitedEmails.length})
          {invitedEmails.length > 0 && (
            <div className={styles.chevron}>
              {isInvitedListOpen ? (
                <ChevronIcon direction="up" />
              ) : (
                <ChevronIcon direction="down" />
              )}
            </div>
          )}
        </div>
        <div
          className={classNames({
            [styles.collapsibleContentOpen]: isInvitedListOpen,
            [styles.collapsibleContentClosed]: !isInvitedListOpen,
          })}
        >
          {invitedEmails.map((email) => (
            <UserRow
              key={email}
              email={email}
              onRemoveUser={async () => {
                await onRemoveInvite(email);
              }}
              dataTestId="invite-row"
            />
          ))}
        </div>
      </>
    );
  };

  const renderCollaborators = () => {
    if (dataset?.sample) {
      return <noscript />;
    }
    return (
      <>
        <div
          className={classNames(styles.collaboratorsTitle, {
            [styles.expandableTitle]: usersWithoutOwner.length > 0,
          })}
          onClick={() => setIsCollaboratorsListOpen(!isCollaboratorsListOpen)}
        >
          <PublicDatasetIcon />
          Collaborators ({usersWithoutOwner.length})
          {usersWithoutOwner.length > 0 && (
            <div className={styles.chevron}>
              {isCollaboratorsListOpen ? (
                <ChevronIcon direction="up" />
              ) : (
                <ChevronIcon direction="down" />
              )}
            </div>
          )}
        </div>
        {isCollaboratorsListOpen &&
          usersWithoutOwner.map((member) => (
            <UserRow
              key={member.user.user_id}
              email={member.user.email}
              name={member.user.name}
              avatarIcon={
                <div>
                  <VLAvatar src={member.user.avatar_uri} />
                </div>
              }
              onRemoveUser={async () => {
                await onRemoveCollaborator(member.user.user_id);
              }}
              dataTestId="collaborator-row"
            />
          ))}
      </>
    );
  };

  const renderPlaceholders = () => {
    if (placeholdersCount === 0) {
      return <noscript />;
    }
    return [...Array(placeholdersCount)].map((_, index) => (
      <div key={index} className={styles.placeholderBox}></div>
    ));
  };

  return (
    <RightSlideMenu
      showRightSlideMenu={showSharingMenu}
      handleCloseIconClick={handleCloseIconClick}
      title="Share this Dataset"
      icon={<ShareIcon />}
      containerRef={containerRef}
    >
      <div
        className={classNames(styles.sharingMenuBody, {
          [styles.hideScroll]: placeholdersCount > 0,
        })}
      >
        <InviteInput
          inputStr={insertedEmail}
          onInputChange={handleInputChange}
          onSubmitInvite={handleSendInvitation}
          inviteBtnDisabled={inviteBtnDisabled}
          inputDisabled={inputDisabled}
        />
        {errorMsg.length > 0 && (
          <div className={styles.sharingMenuInputErrorMsg}>{errorMsg}</div>
        )}
        {renderOwnerRow()}
        {renderInvitations()}
        {renderCollaborators()}
        {renderPlaceholders()}
      </div>
    </RightSlideMenu>
  );
};

const mapStateToProps = (state: State) => {
  return {
    showSharingMenu: getShowDatasetSharingMenu(state),
    dataset: getDataset(state),
    usersWithAccess: getUsersWithAccess(state),
    invitedEmails: getInvitedEmails(state),
  };
};

export default connect(mapStateToProps)(DatasetSharingMenu);
